import { Button, DatePicker, Dropdown, Input, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import CaretDownIcon from "../../../../assets/icons/CaretDownIcon";
import FunnelIcon from "../../../../assets/icons/FunnelIcon";
import GridViewIcon from "../../../../assets/icons/GridViewIcon";
import ListViewIcon from "../../../../assets/icons/ListViewIcon";
import RefreshIcon from "../../../../assets/icons/RefreshIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import ThreeDotsIcon from "../../../../assets/icons/ThreeDotsIcon";
import TicketsIcon from "../../../../assets/icons/TicketsIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import EmptySvg from "../../../../assets/images/empty.svg";
import CustomModal from "../../../../components/ui/CustomModal";
import CustomPagination from "../../../../components/ui/CustomPagination";
import CustomTable from "../../../../components/ui/CustomTable";
import { useGetAllDepartmentQuery } from "../../../../features/superAdmin/employeeService/employeeManagementService";
import { useGetAllFeaturesQuery } from "../../../../features/superAdmin/ticketService/feature/feature.api";
import { useGetAllProductsQuery } from "../../../../features/superAdmin/ticketService/product/product.api";
import { useGetAllTicketLogsQuery } from "../../../../features/superAdmin/ticketService/ticketServiceApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import {
  GetAllTicketsParamsType,
  GetAllTicketsResponseType,
} from "../../../../types/tickets";
import {
  capitalize,
  formatValue,
  removeUnderscore,
} from "../../../../utils/string";
import { addKeyInArray, formatSLA } from "../../../../utils/utils";
import { TEMPLATE_TYPES } from "../constants/ticket.constants";
import ManageTicketForm from "./_components/ManageTicketForm";
import ReopenTicketModal from "./_components/ReopenTicketModal";
import TicketCard from "./_components/TicketCard";
const { RangePicker } = DatePicker;
type EventValue<T> = T | null;
type RangeValue<T> = [EventValue<T>, EventValue<T>] | null;

export const PRIORITY_CONFIG = {
  critical: { className: "status status-danger", title: "P1" },
  major: { className: "status status-orange", title: "P2" },
  moderate: { className: "status status-yellow", title: "P3" },
  minor: { className: "status status-success", title: "P4" },
};

// opened, re_opened, in_progress, approved, rejected, checked, executed, closed, canceled, resolved

export const STATUS_CONFIG = {
  active: "status status-success",
  raised: "status status-success",

  opened: "status status-teal",
  re_opened: "status status-teal",
  in_progress: "status status-success",
  rejected: "status status-danger",
  approved: "status status-primary",
  checked: "status status-warning",
  executed: "status status-info",
  closed: "status status-primary",
  canceled: "status status-dark",
  resolved: "status bg-[#FBEDFD] text-[#CF14EB]",
  forwarded: "status status-dark",
};

export const MY_INVOLVED_CONFIG = {
  "My Ticket": {
    className: "status status-primary",
    link: "ticket-management/all-tickets/creator-details",
  },
  "CC Tickets": {
    className: "status status-info",
    link: "ticket-management/all-tickets/details",
  },
  "My Department": {
    className: "status status-yellow",
    link: "ticket-management/all-tickets/creator-details",
  },
  "Not Involved": {
    className: "status status-dark",
    link: "ticket-management/all-tickets/details",
  },
  "Assignee's Department": {
    className: "status status-indigo",
    link: "ticket-management/all-tickets/approver-details",
  },
};

export default function AllTickets() {
  const { open, handleOpen, handleClose } = useModal<unknown, "RE-OPEN">();
  const user = useAppSelector((state) => state.auth.user);
  const userRole = useAppSelector((state) => state.auth.role);
  const [layout, setLayout] = useState<"grid" | "list">(
    (localStorage.getItem("ticket_layout") as "grid" | "list") || "list",
  );
  const {
    page,
    limit,
    handleLimit,
    handlePagination,
    handleFilter,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetAllTicketsParamsType>({
    defaultFilter: {
      status: "all",
      ticket_type: "",
    },
  });

  const {
    isLoading,
    isFetching,
    isError,
    error,
    data: ticketData,
  } = useGetAllTicketLogsQuery({
    params: {
      ...filter,
      skip: undefined,
      search: keyword,
      status: filter.status === "all" ? undefined : filter.status,
      page,
      ticket_type: filter.ticket_type,
      fromDate: filter.fromDate || undefined,
      toDate: filter.toDate || undefined,
      userRole: userRole === "User" ? userRole : undefined,
    },
  });

  const {
    isError: allDepartmentIsError,
    error: allDepartmentError,
    data: allDepartmentData,
  } = useGetAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100000000,
      status: "active",
    },
  });
  // ------ Queries ------
  const { data: productData, isLoading: isProductLoading } =
    useGetAllProductsQuery({
      params: {
        page: 1,
        limit: 100000,
        status: "active",
      },
    });
  const {
    data: featureData,
    isLoading: isFeatureLoading,
    isFetching: isFeatureFetching,
  } = useGetAllFeaturesQuery(
    {
      params: {
        page: 1,
        limit: 100000,
        status: "active",
        product_id: filter.product_id,
      },
    },
    {
      skip: !filter.product_id,
    },
  );
  // ------ Mapped Select Options ------
  const productOptions = productData?.data?.products?.map((p) => ({
    value: p.id,
    label: p.title,
  }));
  const featureOptions = featureData?.data?.features?.map((p) => ({
    value: p.id,
    label: p.title,
  }));
  // const { data: allSubDepartmentData } = useGetSubDepartmentByIdGroupChatQuery(
  //   filter.department_id as number,
  //   {
  //     skip: !filter.department_id,
  //   },
  // );
  // const subDepartments = allSubDepartmentData?.data?.map((department: any) => {
  //   return {
  //     label: department.name,
  //     value: department.id,
  //   };
  // });

  useError(isError, error, "Get all tickets list fail");
  useError(
    allDepartmentIsError,
    allDepartmentError,
    "Get all department list fail",
  );

  const ticketAllData = ticketData?.data?.ticketLogs;
  const columns: ColumnsType<GetAllTicketsResponseType> = [
    {
      title: "Ticket name",
      width: 200,
      dataIndex: "title",
      render: (val) => {
        return <span className="block min-w-[100px]">{formatValue(val)}</span>;
      },
    },
    {
      title: "Ticket ID",
      align: "center",
      render: (val) => {
        const path =
          MY_INVOLVED_CONFIG?.[
            val?.myInvolved as keyof typeof MY_INVOLVED_CONFIG
          ]?.link;

        return (
          <Link
            className="text-nowrap"
            to={`${
              !!(userRole === "super_admin" || userRole === "Admin")
                ? "/admin/"
                : "/user/"
            }${path}/${val?.generated_ticket_log_id}`}
          >
            {formatValue(val?.generated_ticket_log_id)}
          </Link>
        );
      },
    },
    {
      title: "Department",
      dataIndex: "department",
      align: "center",
      render: (val) => (
        <span className="status status-dark">{formatValue(val?.name)}</span>
      ),
    },
    // {
    //   title: "Sub-Department",
    //   dataIndex: "sub_department",
    //   align: "center",
    //   render: (val) => (
    //     <span className="status status-dark">{formatValue(val?.name)}</span>
    //   ),
    // },
    {
      title: "Category",
      dataIndex: "category",
      align: "center",
      render: (val) => (
        <span className="status status-indigo">{capitalize(val)}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      align: "center",
      render: (val) => (
        <span
          className={`status ${
            val === TEMPLATE_TYPES.sla ? "status-primary" : "status-info"
          }`}
        >
          {val === TEMPLATE_TYPES.sla ? "SLA Based" : "Workflow Based"}
        </span>
      ),
    },
    {
      title: "SLA",
      dataIndex: "totalSLA",
      align: "center",
      render: (val) => (
        <span className="status status-orange">{formatSLA(val)}</span>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      align: "center",
      render: (val) => (
        <span
          className={
            PRIORITY_CONFIG?.[val as keyof typeof PRIORITY_CONFIG].className
          }
        >
          {formatValue(
            PRIORITY_CONFIG?.[val as keyof typeof PRIORITY_CONFIG].title,
          )}
        </span>
      ),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      align: "center",
      render: (val) => (
        <span className="status status-orange">
          {formatValue(capitalize(val))}
        </span>
      ),
    },
    {
      title: "Involvement Type",
      dataIndex: "myInvolved",
      align: "center",
      render: (val) => (
        <span
          className={
            MY_INVOLVED_CONFIG?.[val as keyof typeof MY_INVOLVED_CONFIG]
              ?.className
          }
        >
          {formatValue(val)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "workflow_status",
      align: "center",
      render: (val) => {
        return (
          <span
            className={`status ${
              STATUS_CONFIG?.[val as keyof typeof STATUS_CONFIG]
            }`}
          >
            {
              // val === "raised"
              //   ? "In-Progress"
              //   : val === "active" || val === "opened"
              //   ? "In-Progress"
              //   :
              capitalize(removeUnderscore(val))
            }
          </span>
        );
      },
    },
    {
      title: "Action",
      align: "right",
      render: (val) => {
        const {
          id: userId,
          role: userRole,
          department_id: userDept,
        } = user || {};
        const {
          raised_by,
          raised_by_dept_id: ticketDept,
          workflow_status: status,
        } = val || {};

        //
        const fromSameDept = userDept === ticketDept;
        const isCreator = userId === raised_by;
        const isEndedTicket = status === "canceled" || status === "closed";
        const canReopenTicket = (isCreator || fromSameDept) && isEndedTicket;
        const path =
          MY_INVOLVED_CONFIG?.[
            val?.myInvolved as keyof typeof MY_INVOLVED_CONFIG
          ]?.link;
        return (
          <div className="flex items-center justify-center">
            <Dropdown
              menu={{
                items: [
                  {
                    label: (
                      <Link
                        to={`${
                          !!(userRole === "super_admin") ? "/admin/" : "/user/"
                        }${path}/${val?.generated_ticket_log_id}`}
                      >
                        <div className="flex gap-2 items-center">
                          <span>
                            <ViewIcon />
                          </span>
                          <span>View Details</span>
                        </div>
                      </Link>
                    ),
                    key: "1",
                  },
                  ...(canReopenTicket
                    ? [
                        {
                          label: (
                            <div
                              onClick={() =>
                                handleOpen({ type: "RE-OPEN", data: val.id })
                              }
                              className="flex gap-2 items-center"
                            >
                              <span>
                                <RefreshIcon />
                              </span>
                              <span>Re-Open</span>
                            </div>
                          ),
                          key: "2",
                        },
                      ]
                    : []),
                ],
              }}
              trigger={["click"]}
            >
              <div className="cursor-pointer">
                <ThreeDotsIcon />
              </div>
            </Dropdown>
          </div>
        );
      },
    },
  ];
  useEffect(() => {
    localStorage.setItem("ticket_layout", layout);
  }, [layout]);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const status = searchParams.get("status");
    console.log(status);
    if (status) {
      handleFilter("myInvolve", "my-ticket"); // Your filter logic
      if (status !== "my-ticket") {
        handleFilter("status", status); // Your filter logic
      }

      // Create a copy of current params
      // const newParams = new URLSearchParams(searchParams);
      // newParams.delete("status"); // Remove the param
      // setSearchParams(newParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  return (
    <SidebarLayout title="Ticket Management">
      {open.type === "ADD" && (
        <CustomModal
          title={undefined}
          open={open?.open}
          handleClose={handleClose}
          width={738}
          closable={false}
          footer={null}
        >
          <ManageTicketForm key={1} onClose={handleClose} />
        </CustomModal>
      )}
      {open.type === "RE-OPEN" && (
        <ReopenTicketModal
          open={open?.open}
          handleClose={handleClose}
          id={open?.data as number}
        />
      )}

      <div className="flex justify-between items-center flex-wrap">
        <div className="flex gap-2 items-center mb-2">
          <div>Total Rows</div>
          <Select
            placeholder="10"
            suffixIcon={<CaretDownIcon />}
            size="middle"
            style={{ width: 70 }}
            onChange={handleLimit}
            options={[
              { label: "10", value: 10 },
              { label: "20", value: 20 },
              { label: "50", value: 50 },
              { label: "100", value: 100 },
              { label: "1000", value: 1000 },
            ]}
          />
          <div>of {ticketData?.data?.totalLength || 0}</div>
        </div>

        <div className="flex gap-2 items-center flex-wrap md:flex-nowrap">
          <Input
            placeholder="Search by Name, Id"
            suffix={<SearchIcon />}
            size="middle"
            className="max-w-[300px] lg:min-w-[300px] !h-[36px]"
            onChange={handleSearch}
          />
          <Dropdown
            dropdownRender={() => (
              <div className="p-3 w-[320px] space-y-2 bg-white rounded-lg shadow-md">
                <RangePicker
                  className="w-full !h-[36px]"
                  value={
                    filter.fromDate && filter.toDate
                      ? [dayjs(filter.fromDate), dayjs(filter.toDate)]
                      : null
                  }
                  onChange={(dates: RangeValue<Dayjs>) => {
                    if (dates && dates[0] && dates[1]) {
                      handleFilter("fromDate", dates[0].format("YYYY-MM-DD"));
                      handleFilter("toDate", dates[1].format("YYYY-MM-DD"));
                    } else {
                      handleFilter("fromDate", null as unknown as undefined);
                      handleFilter("toDate", null as unknown as undefined);
                    }
                  }}
                />
                <Select
                  placeholder="Department"
                  className="w-full !h-[36px]"
                  suffixIcon={<CaretDownIcon />}
                  size="middle"
                  value={filter?.department_id ?? undefined}
                  onChange={(value) => {
                    handleFilter("department_id", value);
                    // handleFilter("sub_department_id", undefined);
                  }}
                  options={allDepartmentData?.data?.map((dep) => ({
                    label: dep.name,
                    value: dep.id,
                  }))}
                  allowClear
                />

                <Select
                  placeholder="Category"
                  className="w-full !h-[36px]"
                  suffixIcon={<CaretDownIcon />}
                  size="middle"
                  value={filter?.category ?? undefined}
                  onChange={(value) => handleFilter("category", value)}
                  options={[
                    {
                      label: "General",
                      value: "general",
                    },
                    {
                      label: "Product",
                      value: "product",
                    },
                  ]}
                  allowClear
                />
                {filter?.category === "product" && (
                  <>
                    <Select
                      allowClear
                      suffixIcon={<CaretDownIcon />}
                      placeholder="Product"
                      options={productOptions}
                      disabled={isProductLoading}
                      className="w-full !h-[36px]"
                      onChange={(value) => {
                        handleFilter("product_id", value);
                        handleFilter("feature_id", undefined);
                      }}
                    />
                    <Select
                      allowClear
                      suffixIcon={<CaretDownIcon />}
                      className="w-full !h-[36px]"
                      placeholder="Features"
                      options={featureOptions}
                      value={filter.feature_id}
                      disabled={
                        !filter?.product_id ||
                        isProductLoading ||
                        isFeatureFetching ||
                        isFeatureLoading
                      }
                      onChange={(value) => {
                        handleFilter("feature_id", value);
                      }}
                    />
                  </>
                )}
                <Select
                  placeholder="Involvement Type"
                  className="w-full !h-[36px]"
                  suffixIcon={<CaretDownIcon />}
                  size="middle"
                  value={filter?.myInvolve ?? undefined}
                  onChange={(value) => handleFilter("myInvolve", value)}
                  options={[
                    {
                      label: "My Ticket",
                      value: "my-ticket",
                      // value: "My Ticket",
                    },
                    {
                      label: "CC Tickets",
                      value: "my-cc",
                      // value: "CC Tickets",
                    },
                    {
                      label: "My Department",
                      value: "my-department",
                      // value: "My Department",
                    },
                    {
                      label: "Assignee's Department",
                      value: "assignee-department",
                      // value: "My Department",
                    },
                    ...(userRole !== "User"
                      ? [
                          {
                            label: "Not Involved",
                            value: "not-involved",
                            // value: "My Department",
                          },
                        ]
                      : []),
                  ]}
                  allowClear
                />

                <Select
                  placeholder="Type"
                  className="w-full !h-[36px]"
                  suffixIcon={<CaretDownIcon />}
                  size="middle"
                  // value={filter?.ticket_type ?? undefined}
                  onChange={(value) => handleFilter("ticket_type", value)}
                  options={[
                    { label: "All", value: "" },
                    { label: "SLA Based", value: TEMPLATE_TYPES.sla },
                    { label: "Workflow Based", value: TEMPLATE_TYPES.workflow },
                  ]}
                  allowClear
                />
                <Select
                  placeholder="Severity"
                  className="w-full !h-[36px]"
                  suffixIcon={<CaretDownIcon />}
                  size="middle"
                  // value={filter?.severity ?? undefined}
                  onChange={(value) => handleFilter("severity", value)}
                  options={[
                    { label: "S1", value: "s1" },
                    { label: "S2", value: "s2" },
                    { label: "S3", value: "s3" },
                    { label: "S4", value: "s4" },
                  ]}
                  allowClear
                />

                <Select
                  placeholder="Priority"
                  className="w-full !h-[36px]"
                  suffixIcon={<CaretDownIcon />}
                  size="middle"
                  value={filter?.priority ?? undefined}
                  onChange={(value) => handleFilter("priority", value)}
                  options={[
                    {
                      value: "critical",
                      label: "P1",
                    },
                    {
                      value: "major",
                      label: "P2",
                    },
                    {
                      value: "moderate",
                      label: "P3",
                    },
                    {
                      value: "minor",
                      label: "P4",
                    },
                  ]}
                  allowClear
                />

                <Select
                  placeholder="Status"
                  className="w-full !h-[36px]"
                  suffixIcon={<CaretDownIcon />}
                  size="middle"
                  // value={filter?.status ?? undefined}
                  onChange={(value) => handleFilter("status", value)}
                  options={[
                    { label: "All", value: "all" },
                    { label: "Opened", value: "opened" },
                    { label: "In-Progress", value: "in_progress" },
                    { label: "Resolved", value: "resolved" },
                    { label: "Checked", value: "checked" },
                    { label: "Approved", value: "approved" },
                    { label: "Executed", value: "executed" },
                    { label: "Rejected", value: "rejected" },
                    { label: "Closed", value: "closed" },
                    { label: "Canceled", value: "canceled" },
                  ]}
                  allowClear
                />
              </div>
            )}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button className="button-bordered-light h-9">
              <FunnelIcon />
              Filter
            </Button>
          </Dropdown>

          <Button
            onClick={() => handleOpen({ type: "ADD" })}
            className="button-primary-light h-9"
          >
            <TicketsIcon />
            Create Ticket
          </Button>

          <div
            className={`cursor-pointer ${
              layout === "grid" ? "text-[#FE7235]" : ""
            } text-[18px]`}
            onClick={() => setLayout("grid")}
          >
            <GridViewIcon />
          </div>
          <div
            className={`cursor-pointer ${
              layout === "list" ? "text-[#FE7235]" : ""
            } text-[18px]`}
            onClick={() => setLayout("list")}
          >
            <ListViewIcon />
          </div>
        </div>
      </div>

      {layout === "grid" && (
        <>
          {ticketAllData?.length !== 0 ? (
            <>
              <div className="grid mt-4 xl:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
                {ticketAllData?.map((ticket) => (
                  <TicketCard key={ticket.id} ticket={ticket} />
                ))}
              </div>

              <CustomPagination
                total={ticketData?.data?.totalLength || 0}
                limit={limit}
                page={page}
                handlePagination={handlePagination}
              />
            </>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <img src={EmptySvg} alt="empty_image" />
              </div>
            </>
          )}
        </>
      )}

      {layout === "list" && (
        <div className="mt-4">
          <CustomTable
            loading={isLoading || isFetching}
            limit={limit}
            page={page}
            columns={columns}
            handlePagination={handlePagination}
            total={ticketData?.data?.totalLength || 0}
            dataSource={addKeyInArray(ticketAllData)}
          />
        </div>
      )}
    </SidebarLayout>
  );
}
